/**
 * Single source of truth for dashboard time-range handling.
 *
 * Every surface that offers a "last N hours/days" window (Reports Explorer,
 * Issues, Scripts) parses, validates and resolves its window through here.
 * Two rules matter:
 *
 * 1. **Validate, never cast.** A `?range=` value arrives from a URL a user can
 *    edit or share. An unrecognised preset falls back to the default rather
 *    than indexing `TIME_RANGE_MS` with a key it does not have — which used to
 *    produce `new Date(NaN).toISOString()` and throw during render.
 * 2. **Resolve late.** A selection is a *description* of a window, not a pair
 *    of timestamps. Timestamps are derived at fetch time so a polled query
 *    asks for a window that still includes "now", instead of one frozen at
 *    page load.
 */

export const TIME_RANGE_PRESETS = [
  "15m",
  "1h",
  "4h",
  "24h",
  "7d",
  "30d",
] as const;

export type TimeRangePreset = (typeof TIME_RANGE_PRESETS)[number];

/**
 * Every section's window until the reader picks another (task 453): a week,
 * which is what the Overview's "needs a decision this week" links into.
 * Before, Policy opened on 24 hours, Scripts on 7 days and its deep links on
 * 30, so one issue's count changed with the page it was read on.
 */
export const DEFAULT_TIME_RANGE_PRESET: TimeRangePreset = "7d";

/**
 * The Explorer's own default. It is where a reader goes to watch reports
 * arrive, with a live tail and minute buckets, and a week of raw rows buries
 * the ones that just came in.
 */
export const EXPLORER_TIME_RANGE_PRESET: TimeRangePreset = "24h";

export const TIME_RANGE_MS: Record<TimeRangePreset, number> = {
  "15m": 15 * 60 * 1000,
  "1h": 60 * 60 * 1000,
  "4h": 4 * 60 * 60 * 1000,
  "24h": 24 * 60 * 60 * 1000,
  "7d": 7 * 24 * 60 * 60 * 1000,
  "30d": 30 * 24 * 60 * 60 * 1000,
};

export const TIME_RANGE_LABELS: Record<TimeRangePreset, string> = {
  "15m": "Last 15 minutes",
  "1h": "Last hour",
  "4h": "Last 4 hours",
  "24h": "Last 24 hours",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
};

export const CUSTOM_TIME_RANGE_LABEL = "Custom range";

/** A window the user picked, resolved to timestamps only at fetch time. */
export type TimeRangeSelection =
  | { kind: "preset"; preset: TimeRangePreset }
  | { kind: "custom"; startTime: string; endTime: string };

/** What a picker displays: a preset id, or the custom sentinel. */
export type TimeRangeValue = TimeRangePreset | "custom";

export function isTimeRangePreset(value: unknown): value is TimeRangePreset {
  return (
    typeof value === "string" &&
    (TIME_RANGE_PRESETS as readonly string[]).includes(value)
  );
}

/**
 * Coerce an untrusted `range` value (URL param, stored preference) to a known
 * preset. Anything unrecognised — including `null` and `undefined` — yields
 * the fallback.
 */
export function parseTimeRangePreset(
  value: string | null | undefined,
  fallback: TimeRangePreset = DEFAULT_TIME_RANGE_PRESET,
): TimeRangePreset {
  return isTimeRangePreset(value) ? value : fallback;
}

function isParsableDate(value: string | null | undefined): value is string {
  if (!value) return false;
  return !Number.isNaN(new Date(value).getTime());
}

export interface TimeRangeParams {
  range?: string | null;
  startTime?: string | null;
  endTime?: string | null;
}

/**
 * Build a selection from raw params. A custom range is only honoured when both
 * bounds parse as dates and start precedes end; otherwise the `range` preset
 * (or the fallback) wins.
 */
export function parseTimeRangeSelection(
  params: TimeRangeParams,
  fallback: TimeRangePreset = DEFAULT_TIME_RANGE_PRESET,
): TimeRangeSelection {
  const { startTime, endTime } = params;

  if (isParsableDate(startTime) && isParsableDate(endTime)) {
    if (new Date(startTime).getTime() <= new Date(endTime).getTime()) {
      return { kind: "custom", startTime, endTime };
    }
  }

  return {
    kind: "preset",
    preset: parseTimeRangePreset(params.range, fallback),
  };
}

/** Read a selection straight out of URL search params. */
export function parseTimeRangeFromSearchParams(
  searchParams: URLSearchParams,
  fallback: TimeRangePreset = DEFAULT_TIME_RANGE_PRESET,
): TimeRangeSelection {
  return parseTimeRangeSelection(
    {
      range: searchParams.get("range"),
      startTime: searchParams.get("startTime"),
      endTime: searchParams.get("endTime"),
    },
    fallback,
  );
}

export interface TimeWindow {
  startTime: string;
  endTime: string;
}

/**
 * Turn a selection into concrete ISO timestamps.
 *
 * Call this inside a `queryFn`, not while building a query key: a preset
 * window must be anchored to the moment of the request so polling sees new
 * data. `anchorMs` lets a paginated query pin every page to one anchor.
 */
export function resolveTimeWindow(
  selection: TimeRangeSelection,
  anchorMs: number = Date.now(),
): TimeWindow {
  if (selection.kind === "custom") {
    return { startTime: selection.startTime, endTime: selection.endTime };
  }

  const end = new Date(anchorMs);
  const start = new Date(anchorMs - TIME_RANGE_MS[selection.preset]);
  return { startTime: start.toISOString(), endTime: end.toISOString() };
}

/** The value a time-range picker should show as selected. */
export function timeRangeValue(selection: TimeRangeSelection): TimeRangeValue {
  return selection.kind === "custom" ? "custom" : selection.preset;
}

export function timeRangeLabel(value: TimeRangeValue): string {
  return value === "custom"
    ? CUSTOM_TIME_RANGE_LABEL
    : TIME_RANGE_LABELS[value];
}

/** Convenience for a picker's `startTime`/`endTime` display props. */
export function customBounds(
  selection: TimeRangeSelection,
): Partial<TimeWindow> {
  return selection.kind === "custom"
    ? { startTime: selection.startTime, endTime: selection.endTime }
    : {};
}
